import { useState } from 'react';
import { Circle, MapContainer, Marker, Polygon, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Alert, api, Badge, Button, fmtDate, Input, Select, Spinner, useAuth, useFetch } from '@greenrinse/ui';

export interface CheckResult { check: string; passed: boolean; detail: string | null }
export interface DamageItem {
  id: string; description: string; location: string | null; preExisting: boolean;
  source: 'Washer' | 'Ai'; status: 'Reported' | 'Suggested' | 'Confirmed' | 'Dismissed'; severity: 'Unknown' | 'Minor' | 'Moderate' | 'Major';
  damageType: string | null; timing: 'PreExisting' | 'New' | 'Unclear' | null; confidence: number | null;
}

/** Washer reports and AI suggestions; supervisors confirm or dismiss AI suggestions (partners only ever see confirmed ones). */
function DamageList({ recordId, items, canReview, onUpdated }: { recordId: string; items: DamageItem[]; canReview: boolean; onUpdated: (r: RecordDetailDto) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const decide = async (d: DamageItem, confirm: boolean) => {
    setBusy(d.id); setError(null);
    try { onUpdated(await api<RecordDetailDto>(`/api/records/${recordId}/damage/${d.id}/decision`, { method: 'POST', body: { confirm, note: null } })); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };
  return (
    <div className="gr-stack" style={{ gap: 6 }}>
      {error && <Alert tone="error">{error}</Alert>}
      {items.map((d) => (
        <div key={d.id} style={{ opacity: d.status === 'Dismissed' ? 0.55 : 1 }}>
          <span style={{ textDecoration: d.status === 'Dismissed' ? 'line-through' : undefined }}>{d.location ? <strong>{d.location}: </strong> : null}{d.description}</span>{' '}
          {d.source === 'Ai' ? <Badge tone="accent">AI{d.confidence != null ? ` ${d.confidence}%` : ''}</Badge> : <Badge>Washer</Badge>}{' '}
          {d.timing === 'New' ? <Badge tone="error">New after wash</Badge> : d.timing === 'Unclear' ? <Badge>Unclear</Badge> : <Badge tone="success">Before wash</Badge>}{' '}
          {d.severity !== 'Unknown' && <Badge tone={d.severity === 'Major' ? 'error' : d.severity === 'Moderate' ? 'warning' : 'default'}>{d.severity}</Badge>}{' '}
          {d.status === 'Confirmed' && <Badge tone="success">Confirmed</Badge>}
          {d.status === 'Dismissed' && <Badge>Dismissed</Badge>}
          {d.status === 'Suggested' && (canReview
            ? <span className="gr-row" style={{ display: 'inline-flex', gap: 6, marginInlineStart: 6 }}>
                <Button variant="tonal" loading={busy === d.id} onClick={() => void decide(d, true)} style={{ minHeight: 28, padding: '0 10px' }}>Confirm</Button>
                <Button variant="ghost" disabled={busy === d.id} onClick={() => void decide(d, false)} style={{ minHeight: 28, padding: '0 10px' }}>Dismiss</Button>
              </span>
            : <Badge tone="warning">Awaiting review</Badge>)}
        </div>
      ))}
    </div>
  );
}
export interface RecordPhoto { id: string; kind: 'Before' | 'After' | 'Damage'; sequence: number; takenAtUtc: string; lat: number | null; lng: number | null; accuracy: number | null; isUploaded: boolean; url: string | null; thumbUrl: string | null; stampedUrl: string | null }
export interface RecordDetailDto {
  id: string; status: string; flags: number; flagNames: string[]; checks: CheckResult[];
  plateEntered: string; plateNormalized: string; plateOcrBefore: string | null; plateOcrBeforeConfidence: number | null; plateOcrAfter: string | null; plateOcrAfterConfidence: number | null; plateEditedByWasher: boolean;
  fleetVehicleId: string | null; fleetPlate: string | null; fleetMakeModel: string | null; isUnlisted: boolean; isFuzzyMatch: boolean; vehicleType: string; vehicleColor: string | null; service: string; waterLitres: number | null;
  aiVehicleType: string | null; aiVehicleTypeConfidence: number | null; aiVehicleColor: string | null; aiMakeModel: string | null;
  cleanlinessScore: number | null; cleanlinessVerdict: string | null;
  siteId: string; siteName: string; siteLat: number; siteLng: number; siteRadiusMeters: number; sitePolygon: number[][] | null;
  partnerId: string | null; partnerName: string | null; contractId: string | null; contractName: string | null; shiftId: string | null; teamName: string | null; washerUserId: string; washerName: string;
  beforeAtUtc: string; afterAtUtc: string | null; receivedAtUtc: string; verifiedAtUtc: string | null; siteLocalDate: string; cycleIndex: number | null;
  lat: number | null; lng: number | null; gpsAccuracyMeters: number | null; distanceFromSiteMeters: number | null; notes: string | null; clientInfo: string | null;
  photos: RecordPhoto[]; damage: DamageItem[];
  reviewedByUserId: string | null; reviewedByName: string | null; reviewedAtUtc: string | null; reviewReason: string | null; createdAtUtc: string;
}

export const FLAG_LABELS: Record<string, string> = {
  OutsideSite: 'Outside site', TimeMismatch: 'Time mismatch', TooFast: 'Too fast', PlateMismatch: 'Plate mismatch', Unlisted: 'Unlisted vehicle',
  PossibleMismatch: 'Possible plate mismatch', Duplicate: 'Duplicate', ReusedPhoto: 'Reused photo', ClassMismatch: 'Class mismatch', MissingPhotos: 'Missing photos', OutsideWorkingHours: 'Outside working hours',
  PoorPhotoQuality: 'Poor photo quality', NoVisibleChange: 'No visible change', LowCleanliness: 'Low cleanliness', NewDamage: 'Possible new damage',
};
export function statusTone(s: string): 'default' | 'success' | 'warning' | 'error' | 'accent' {
  return s === 'Verified' || s === 'Approved' ? 'success' : s === 'Flagged' ? 'warning' : s === 'Rejected' ? 'error' : 'default';
}

const pin = L.divIcon({ className: '', html: '<div style="width:16px;height:16px;border-radius:50%;background:#BA1A1A;border:3px solid #fff;box-shadow:0 0 0 2px #BA1A1A"></div>', iconSize: [16, 16], iconAnchor: [8, 8] });

export function RecordDetail({ id, onChanged }: { id: string; onChanged?: () => void }) {
  const { user } = useAuth();
  const canReview = ['SuperAdmin', 'RegionalManager', 'OperationsSupervisor'].includes(user?.role ?? '');
  const rec = useFetch<RecordDetailDto>(`/api/records/${id}`);
  const [action, setAction] = useState<'approve' | 'reject' | 'correct' | null>(null);
  const [reason, setReason] = useState('');
  const [plate, setPlate] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [service, setService] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stamped, setStamped] = useState(false);

  if (rec.loading) return <Spinner />;
  if (!rec.data) return <Alert tone="error">{rec.error ?? 'Not found'}</Alert>;
  const r = rec.data;
  const decided = r.status === 'Approved' || r.status === 'Rejected';

  const act = async (path: string, body: unknown) => {
    setBusy(true); setError(null);
    try {
      const updated = await api<RecordDetailDto>(`/api/records/${id}/${path}`, { method: 'POST', body });
      rec.setData(updated); setAction(null); setReason(''); onChanged?.();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const before = r.photos.filter((p) => p.kind === 'Before');
  const after = r.photos.filter((p) => p.kind === 'After');
  const damagePhotos = r.photos.filter((p) => p.kind === 'Damage');
  const photoUrl = (p: RecordPhoto) => (stamped && p.stampedUrl) || p.url;
  const gps = r.lat != null && r.lng != null ? ([r.lat, r.lng] as [number, number]) : null;

  return (
    <div className="gr-stack">
      <div className="gr-row" style={{ justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontFamily: 'var(--gr-font-heading)', fontWeight: 800, fontSize: 24, letterSpacing: '0.08em' }}>{r.plateEntered}</div>
          <div className="gr-muted">{r.siteName} · {r.partnerName} · {r.washerName}{r.teamName ? ` (${r.teamName})` : ''}</div>
        </div>
        <div className="gr-row"><Badge tone={statusTone(r.status)}>{r.status}</Badge>{r.flagNames.map((f) => <Badge key={f} tone="warning">{FLAG_LABELS[f] ?? f}</Badge>)}</div>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      {decided && <Alert tone={r.status === 'Approved' ? 'success' : 'error'}>{r.status} by {r.reviewedByName ?? '—'} on {fmtDate(r.reviewedAtUtc)}: {r.reviewReason}</Alert>}

      {/* Photos side by side */}
      <div className="gr-row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Evidence</h3>
        <label className="gr-row" style={{ fontSize: 14 }}><input type="checkbox" checked={stamped} onChange={(e) => setStamped(e.target.checked)} /> Show stamped copies</label>
      </div>
      <div className="gr-form-grid">
        <PhotoPanel title="Before" photos={before} url={photoUrl} />
        <PhotoPanel title="After" photos={after} url={photoUrl} />
        {damagePhotos.length > 0 && <div className="gr-span-2"><PhotoPanel title="Damage" photos={damagePhotos} url={photoUrl} /></div>}
      </div>

      {/* Map + facts */}
      <div className="gr-form-grid">
        <div style={{ height: 260, borderRadius: 16, overflow: 'hidden', border: '1px solid var(--gr-border)' }}>
          <MapContainer center={gps ?? [r.siteLat, r.siteLng]} zoom={16} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {r.sitePolygon ? <Polygon positions={r.sitePolygon.map((p) => [p[0], p[1]] as [number, number])} pathOptions={{ color: '#2F6B4F', fillOpacity: 0.2 }} />
              : <Circle center={[r.siteLat, r.siteLng]} radius={r.siteRadiusMeters} pathOptions={{ color: '#2C9A8E', fillOpacity: 0.2 }} />}
            {gps && <Marker position={gps} icon={pin} />}
            {gps && r.gpsAccuracyMeters && <Circle center={gps} radius={r.gpsAccuracyMeters} pathOptions={{ color: '#BA1A1A', weight: 1, fillOpacity: 0.08 }} />}
          </MapContainer>
        </div>
        <dl className="gr-kv">
          <dt>Before</dt><dd>{fmtDate(r.beforeAtUtc)}</dd>
          <dt>After</dt><dd>{fmtDate(r.afterAtUtc)} {r.afterAtUtc && <span className="gr-muted">({Math.round((Date.parse(r.afterAtUtc) - Date.parse(r.beforeAtUtc)) / 1000)} s)</span>}</dd>
          <dt>Received</dt><dd>{fmtDate(r.receivedAtUtc)}</dd>
          <dt>Site date</dt><dd>{r.siteLocalDate}{r.cycleIndex != null && <span className="gr-muted"> · cycle #{r.cycleIndex}</span>}</dd>
          <dt>GPS</dt><dd>{gps ? `${gps[0].toFixed(5)}, ${gps[1].toFixed(5)} ±${Math.round(r.gpsAccuracyMeters ?? 0)} m` : '—'}{r.distanceFromSiteMeters != null && <span className="gr-muted"> · {r.distanceFromSiteMeters} m outside fence</span>}</dd>
          <dt>Vehicle</dt><dd>{r.vehicleType}{r.vehicleColor ? ` · ${r.vehicleColor}` : ''} {r.fleetVehicleId ? <Badge tone={r.isFuzzyMatch ? 'warning' : 'success'}>{r.isFuzzyMatch ? `fuzzy → ${r.fleetPlate}` : `fleet: ${r.fleetMakeModel || r.fleetPlate}`}</Badge> : <Badge tone="warning">unlisted</Badge>}</dd>
          <dt>Service</dt><dd>{r.service}{r.waterLitres != null ? ` · ${r.waterLitres} L` : ''}</dd>
          {r.cleanlinessScore != null && <><dt>AI cleanliness</dt><dd><Badge tone={r.cleanlinessVerdict === 'Clean' ? 'success' : r.cleanlinessVerdict === 'Acceptable' ? 'accent' : 'warning'}>{r.cleanlinessScore}/100 · {r.cleanlinessVerdict}</Badge></dd></>}
          {r.aiVehicleType && <><dt>AI class</dt><dd>{r.aiVehicleType} ({Math.round(r.aiVehicleTypeConfidence ?? 0)}%){r.aiVehicleColor ? ` · ${r.aiVehicleColor}` : ''}{r.aiMakeModel ? ` · ${r.aiMakeModel}` : ''} {r.aiVehicleType !== r.vehicleType && r.aiVehicleType !== 'Unknown' && <Badge tone="warning">≠ {r.vehicleType}</Badge>}</dd></>}
          <dt>AI plate</dt><dd>{r.plateOcrBefore ? `before ${r.plateOcrBefore} (${Math.round(r.plateOcrBeforeConfidence ?? 0)}%)` : '—'}{r.plateOcrAfter ? ` · after ${r.plateOcrAfter} (${Math.round(r.plateOcrAfterConfidence ?? 0)}%)` : ''}{r.plateEditedByWasher && <Badge tone="accent">edited by washer</Badge>}</dd>
          <dt>Contract</dt><dd>{r.contractName ?? '—'}</dd>
          {r.notes && <><dt>Notes</dt><dd>{r.notes}</dd></>}
          {r.damage.length > 0 && <><dt>Damage</dt><dd><DamageList recordId={r.id} items={r.damage} canReview={canReview} onUpdated={(u) => { rec.setData(u); onChanged?.(); }} /></dd></>}
          <dt>Client</dt><dd className="gr-muted" style={{ fontSize: 12 }}>{r.clientInfo ?? '—'}</dd>
        </dl>
      </div>

      {/* Checks */}
      <h3 style={{ margin: 0 }}>Automatic checks {r.verifiedAtUtc && <span className="gr-muted" style={{ fontWeight: 400, fontSize: 13 }}>· {fmtDate(r.verifiedAtUtc)}</span>}</h3>
      {r.checks.length === 0 ? <p className="gr-muted">Not verified yet.</p> : (
        <table className="gr-table">
          <tbody>
            {r.checks.map((c) => (
              <tr key={c.check}>
                <td style={{ width: 28 }}>{c.passed ? '✅' : '❌'}</td>
                <td style={{ fontWeight: 600, width: 160 }}>{c.check}</td>
                <td className={c.passed ? 'gr-muted' : ''} style={{ color: c.passed ? undefined : 'var(--gr-error)' }}>{c.detail ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Actions */}
      {canReview && (
        <div className="gr-card gr-card--tonal gr-card--sm">
          {!action ? (
            <div className="gr-row">
              {!decided && <Button onClick={() => setAction('approve')}>Approve</Button>}
              {!decided && <Button variant="danger" onClick={() => setAction('reject')}>Reject</Button>}
              <Button variant="outline" onClick={() => { setPlate(r.plateEntered); setVehicleType(r.vehicleType); setService(r.service); setAction('correct'); }}>Correct…</Button>
              <Button variant="ghost" loading={busy} onClick={() => void act('reverify', {})}>Re-run checks</Button>
            </div>
          ) : (
            <div className="gr-stack">
              <strong>{action === 'approve' ? 'Approve record' : action === 'reject' ? 'Reject record' : 'Correct record'}</strong>
              {action === 'correct' && (
                <div className="gr-form-grid">
                  <Input label="Plate" plate value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} />
                  <Select label="Vehicle type" value={vehicleType} onChange={(e) => setVehicleType(e.target.value)} options={['Unknown', 'Sedan', 'Suv', 'Van', 'Pickup', 'Truck', 'Bus', 'Motorcycle'].map((v) => ({ value: v, label: v }))} />
                  <Select label="Service" value={service} onChange={(e) => setService(e.target.value)} options={['Exterior', 'Interior', 'Full'].map((v) => ({ value: v, label: v }))} />
                </div>
              )}
              <Input label="Reason (audit-logged)" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
              <div className="gr-row">
                <Button variant="outline" onClick={() => setAction(null)}>Cancel</Button>
                <Button loading={busy} disabled={!reason.trim()} variant={action === 'reject' ? 'danger' : 'primary'}
                  onClick={() => void act(action === 'correct' ? 'correct' : action, action === 'correct'
                    ? { reason, plate: plate !== r.plateEntered ? plate : null, vehicleType: vehicleType !== r.vehicleType ? vehicleType : null, service: service !== r.service ? service : null, approveAfter: true }
                    : { reason })}>
                  {action === 'approve' ? 'Approve' : action === 'reject' ? 'Reject' : 'Save & approve'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PhotoPanel({ title, photos, url }: { title: string; photos: RecordPhoto[]; url: (p: RecordPhoto) => string | null }) {
  const [idx, setIdx] = useState(0);
  const p = photos[idx];
  return (
    <div>
      <div className="gr-row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
        <strong>{title}</strong>
        {p && <span className="gr-muted" style={{ fontSize: 12 }}>{fmtDate(p.takenAtUtc)}{p.lat != null ? ` · ${p.lat.toFixed(4)}, ${p.lng?.toFixed(4)}` : ''}</span>}
      </div>
      {!p ? <div className="gr-card gr-card--tonal gr-card--sm gr-muted" style={{ textAlign: 'center' }}>No photo</div> : !p.isUploaded || !url(p) ? (
        <div className="gr-card gr-card--tonal gr-card--sm gr-muted" style={{ textAlign: 'center' }}>Not uploaded yet</div>
      ) : (
        <a href={url(p)!} target="_blank" rel="noreferrer"><img src={url(p)!} alt={title} style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 16, border: '1px solid var(--gr-border)' }} /></a>
      )}
      {photos.length > 1 && (
        <div className="gr-row" style={{ marginTop: 6 }}>
          {photos.map((ph, i) => <img key={ph.id} src={ph.thumbUrl ?? ''} alt="" onClick={() => setIdx(i)} style={{ width: 56, height: 42, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', outline: i === idx ? '2px solid var(--gr-accent)' : 'none' }} />)}
        </div>
      )}
    </div>
  );
}
